import React, { useEffect, useState } from "react";
import AppWrapper from "../AppWrapper";
import {
  Badge,
  BlockStack,
  Button,
  IndexTable,
  LegacyCard,
  Modal,
  Page,
  Spinner,
  Text,
  Pagination,
  InlineStack,
  Banner,
  Checkbox,
  TextField,
  Popover,
  ChoiceList,
} from "@shopify/polaris";
import { SettingsIcon } from "@shopify/polaris-icons";
import supabase from "../../supabase-client";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { UserAuth } from "../../context/AuthContext";
import {
  formatDate,
  formatShortDate,
  formatPrice,
  numberWithCommas,
} from "../../utility/funcs";
import { validateServiceForDispersion } from "../../utility/dispersion";
import { authorizePayouts, speiEnabled, messageFor, getFunds } from "../../utility/pagos";
import ClabeBadge from "./ClabeBadge";
import { useProviderLive } from "../../utility/useProviderLive";
import StatusBadge from "./StatusBadge";


// Configurable columns for the services table (persisted in localStorage,
// same pattern as the reservations table). "id" is always shown.
const COLUMN_DEFS = [
  { key: "id", title: "ID", required: true },
  { key: "plate", title: "Placa" },
  { key: "vin", title: "VIN" },
  { key: "brand", title: "Marca" },
  { key: "model", title: "Modelo" },
  { key: "provider", title: "Proveedor" },
  { key: "date", title: "Fecha" },
  { key: "odometer", title: "Odómetro" },
  { key: "total", title: "Total" },
  { key: "status", title: "Status" },
];
// Default visible set: everything except the extra VIN column (opt-in).
const DEFAULT_COLUMNS = COLUMN_DEFS.filter((c) => c.key !== "vin").map(
  (c) => c.key
);
const COLUMNS_STORAGE_KEY = "services_visible_columns_v1";

// Status filter (empty = all). Same labels as StatusBadge.
const STATUS_CHOICES = [
  { label: "Esperando proveedor", value: "awaiting_provider" },
  { label: "Falta factura", value: "awaiting_invoice" },
  { label: "En revisión", value: "pending_approval" },
  { label: "Falta pago", value: "payment_pending" },
  { label: "Pago parcial", value: "partially_paid" },
  { label: "Pagado", value: "paid" },
  { label: "Negado", value: "denied" },
];
const STATUS_STORAGE_KEY = "services_status_filter_v1";

function loadStoredStatuses() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STATUS_STORAGE_KEY) || "[]");
    const known = new Set(STATUS_CHOICES.map((c) => c.value));
    return Array.isArray(parsed) ? parsed.filter((v) => known.has(v)) : [];
  } catch {
    return [];
  }
}

function loadStoredColumns() {
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (!raw) return DEFAULT_COLUMNS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_COLUMNS;
    // keep only known keys (plus required), preserving canonical order
    const known = new Set(parsed);
    const ordered = COLUMN_DEFS.filter(
      (c) => known.has(c.key) || c.required
    ).map((c) => c.key);
    return ordered.length ? ordered : DEFAULT_COLUMNS;
  } catch {
    return DEFAULT_COLUMNS;
  }
}

function AllServices() {
  const navigate = useNavigate();
  const { hasPermission } = UserAuth();
  const [searchParams] = useSearchParams();
  const providerId = searchParams.get("provider");

  const [services, setServices] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [visibleColumns, setVisibleColumns] = useState(loadStoredColumns);
  const [columnsPopoverOpen, setColumnsPopoverOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState(loadStoredStatuses);
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);

  const handleStatusChange = (selected) => {
    setStatusFilter(selected);
    setPage(1);
    try {
      localStorage.setItem(STATUS_STORAGE_KEY, JSON.stringify(selected));
    } catch {
      // ignore storage errors
    }
  };
  const limit = 20;

  const handleColumnsChange = (selected) => {
    // always keep required columns, preserve canonical order
    const withRequired = COLUMN_DEFS.filter(
      (c) => c.required || selected.includes(c.key)
    ).map((c) => c.key);
    setVisibleColumns(withRequired);
    try {
      localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(withRequired));
    } catch {
      // ignore storage errors (quota, private mode, etc.)
    }
  };

  // Debounce the text box → `search`, and jump back to page 1 on a new term.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // "Por Pagar" modal — pending-payment services with copy-to-clipboard cells.
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingRows, setPendingRows] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // "Falta Factura" modal — services that were quoted, approved and paid, and
  // whose CFDI has not arrived. Kept in front of staff with the age of each one,
  // because a pending-invoice list nobody looks at becomes permanent.
  const [missingOpen, setMissingOpen] = useState(false);
  const [missingRows, setMissingRows] = useState([]);
  const [missingLoading, setMissingLoading] = useState(false);

  const openMissingInvoices = async () => {
    setMissingOpen(true);
    setMissingLoading(true);
    const { data, error } = await supabase
      .from("services")
      .select(
        "id, quoted_total, created_at, status, provider:service_providers(name), car:cars(plate), sublease_plate"
      )
      .eq("invoice_status", "pending")
      // Anticipos only (quotes and quoted link services); a link service
      // waiting for its CFDI with no quote has its own status ("Falta
      // factura") and was never paid up front.
      .not("quoted_total", "is", null)
      .order("created_at", { ascending: true });
    if (error) {
      console.error(error);
      setMissingRows([]);
    } else {
      setMissingRows(data || []);
    }
    setMissingLoading(false);
  };

  // A row is payable only if its provider's CLABE was verified against the
  // banking system. Unverified rows are neither selected nor selectable — the
  // server would refuse them anyway, so offering the choice only invites a
  // failed batch.
  const isSelectable = (r) =>
    ["verified", "verified_override"].includes(r?.provider?.clabe_status);

  // A row that was un-payable can become payable while this modal is open —
  // verification takes ~78s. Fold the change in so the badge updates and the
  // checkbox enables itself, instead of the list going stale mid-backfill.
  // Only subscribed while the modal is open.
  useProviderLive((row) => {
    setPendingRows((prev) =>
      prev.map((r) =>
        r.provider && String(r.provider.id) === String(row.id)
          ? {
              ...r,
              provider: {
                ...r.provider,
                clabe_status: row.clabe_status,
                bank_holder_name: row.bank_holder_name,
              },
            }
          : r
      )
    );
  }, pendingOpen);

  const toggleId = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const selectableRows = pendingRows.filter(isSelectable);
  const allSelected =
    selectableRows.length > 0 && selectableRows.every((r) => selectedIds.has(r.id));
  const someSelected = selectableRows.some((r) => selectedIds.has(r.id));
  const toggleAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(selectableRows.map((r) => r.id)));

  const openPending = async () => {
    setPendingOpen(true);
    setPendingLoading(true);
    const { data, error } = await supabase
      .from("services")
      .select(
        "id, invoice_id, total, performed_at, provider:service_providers(id, name, rfc, banco, clabe, clabe_status, bank_holder_name), car:cars(plate), sublease_plate"
      )
      .eq("status", "payment_pending");
    if (error) {
      console.error(error);
      setPendingRows([]);
      setSelectedIds(new Set());
    } else {
      const rows = data || [];
      setPendingRows(rows);
      // Default to every VERIFIED row; unverified ones start unselected.
      setSelectedIds(new Set(rows.filter(isSelectable).map((r) => r.id)));
    }
    setPendingLoading(false);
  };

  const copyValue = async (value, key) => {
    try {
      await navigator.clipboard.writeText(String(value ?? ""));
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1200);
    } catch (e) {
      console.error(e);
    }
  };

  const formatMxDate = (s) => {
    if (!s) return "";
    return String(s).substring(0, 10);
  };

  // Authorize the selected services as SPEI payouts.
  //
  // One human gesture, N independent payouts: each row gets its own idempotency
  // key and its own outcome server-side, so a single bad row cannot take the
  // batch down. Only verified-CLABE rows are sent; the rest are reported back.
  const [speiSubmitting, setSpeiSubmitting] = useState(false);
  const [speiResult, setSpeiResult] = useState(null);
  const [funds, setFunds] = useState(null);

  // The balance has to be checked against the SUM of what is selected, not each
  // row on its own: ten $4,000 payouts each clear a $23,401 balance while
  // together they need $40,000. Fetched live whenever the modal opens or the
  // selection changes, because a cached balance reads as reassurance.
  useEffect(() => {
    if (!pendingOpen || !speiEnabled()) return;
    let cancelled = false;
    getFunds()
      .then((f) => { if (!cancelled) setFunds(f); })
      .catch(() => { if (!cancelled) setFunds(null); });
    return () => { cancelled = true; };
  }, [pendingOpen, speiSubmitting]);

  // Exactly `isSelectable`, and it must stay that way. These were two separate
  // predicates and they drifted: selection accepted `verified_override` while
  // this one demanded `verified`, so a manually verified provider's rows ticked,
  // counted as selected, and were then dropped from the batch with no error and
  // no request sent. Services 1187, 1188, 1208 and 1209 ($27,943.50) looked paid
  // to the person who clicked and had never been asked for.
  const payableRows = () =>
    pendingRows.filter((r) => selectedIds.has(r.id) && isSelectable(r));

  const selectedTotalCents = () =>
    payableRows().reduce((sum, r) => sum + Math.round(Number(r.total || 0) * 100), 0);

  const fundsShort = () =>
    funds?.available_cents != null && selectedTotalCents() > funds.available_cents;

  const paySelectedWithSpei = async () => {
    const rows = payableRows();
    if (rows.length === 0 || fundsShort()) return;
    setSpeiSubmitting(true);
    setSpeiResult(null);
    try {
      const { results } = await authorizePayouts(
        rows.map((r) => ({ payable_kind: "service", payable_id: String(r.id) }))
      );
      const ok = results.filter((x) => x.ok);
      const failed = results.filter((x) => !x.ok);
      setSpeiResult({ ok: ok.length, failed });
      // Drop the authorized ones from the list; they are no longer "por pagar".
      const okIds = new Set(ok.map((x) => Number(x.payable_id)));
      setPendingRows((prev) => prev.filter((r) => !okIds.has(r.id)));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        okIds.forEach((id) => next.delete(id));
        return next;
      });
    } catch (err) {
      setSpeiResult({ ok: 0, failed: [{ error: err.code, message: err.message }] });
    } finally {
      setSpeiSubmitting(false);
    }
  };

  // Generate + download the Banco BASE dispersión file for the pending services.
  // Rows that can't be dispersed (bad CLABE / RFC / amount) — surfaced live.
  const dispersionIssues = pendingRows
    .map((s) => ({ s, reasons: validateServiceForDispersion(s) }))
    .filter((x) => x.reasons.length > 0);

  useEffect(() => {
    const fetchServices = async () => {
      setLoading(true);

      const from = (page - 1) * limit;
      const to = from + limit - 1;

      let query = supabase
        .from("services")
        .select(
          "id, performed_at, odometer, status, invoice_status, total, car(plate, vin), car_model(name), car_brand(name), provider:service_providers(name), sublease_plate, sublease_vin",
          { count: "exact" },
        )
        .order("performed_at", { ascending: false })
        .order("id", { ascending: false });

      if (providerId) {
        query = query.eq("provider", providerId);
      }
      if (statusFilter.length) {
        // "Falta factura" mirrors the badge in the table: either the status
        // itself, or any service (other than awaiting_provider) whose CFDI is
        // still pending — e.g. paid up front, invoice not received yet.
        if (statusFilter.includes("awaiting_invoice")) {
          const conds = [
            `status.in.(${statusFilter.join(",")})`,
            "and(invoice_status.eq.pending,status.neq.awaiting_provider)",
            // Cancelled at SAT by the provider: needs its replacement CFDI.
            "invoice_status.eq.cancelled",
          ];
          query = query.or(conds.join(","));
        } else {
          query = query.in("status", statusFilter);
        }
      }

      // Cross-table search: plate/VIN live on `cars`, model/brand/provider are
      // FKs, so resolve matching ids first, then OR them (plus sublease text)
      // against the services rows.
      if (search) {
        const like = `%${search}%`;
        const [cars, provs, models, brands] = await Promise.all([
          supabase.from("cars").select("id").or(`plate.ilike.${like},vin.ilike.${like}`),
          supabase.from("service_providers").select("id").ilike("name", like),
          supabase.from("car_models").select("id").ilike("name", like),
          supabase.from("car_brands").select("id").ilike("name", like),
        ]);
        const idList = (r) => (r.data || []).map((x) => x.id);
        const ors = [];
        const carIds = idList(cars); if (carIds.length) ors.push(`car.in.(${carIds.join(",")})`);
        const provIds = idList(provs); if (provIds.length) ors.push(`provider.in.(${provIds.join(",")})`);
        const modelIds = idList(models); if (modelIds.length) ors.push(`car_model.in.(${modelIds.join(",")})`);
        const brandIds = idList(brands); if (brandIds.length) ors.push(`car_brand.in.(${brandIds.join(",")})`);
        ors.push(`sublease_plate.ilike.${like}`, `sublease_vin.ilike.${like}`);
        query = query.or(ors.join(","));
      }

      query = query.range(from, to);

      const { data, error } = await query;

      if (error) {
        console.error(error);
        setLoading(false);
        return;
      }

      setServices(data);
      setLoading(false);
    };

    fetchServices();
  }, [page, search, providerId, statusFilter]);

  const renderCell = (key, service) => {
    switch (key) {
      case "id":
        // A real link so right-click / Cmd-click can open the service in a new
        // tab; a plain click stays in-app (the row's own click would reload).
        return (
          <a
            href={`/services/${service.id}`}
            style={{ color: "inherit", textDecoration: "none" }}
            onClick={(e) => {
              e.stopPropagation();
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              e.preventDefault();
              navigate(`/services/${service.id}`);
            }}
          >
            <Text tone="subdued">#{service.id}</Text>
          </a>
        );
      case "plate":
        return (
          <InlineStack gap="150" blockAlign="center">
            <Text>{service.car?.plate || service.sublease_plate}</Text>
            {!service.car && service.sublease_plate && (
              <Badge tone="magic" size="small">Sub</Badge>
            )}
          </InlineStack>
        );
      case "vin":
        return <Text>{service.car?.vin || service.sublease_vin || "—"}</Text>;
      case "brand":
        return <Text truncate>{service.car_brand?.name || "—"}</Text>;
      case "model":
        return <Text truncate>{service.car_model?.name || "—"}</Text>;
      case "provider":
        return (
          <div
            style={{ width: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            title={service.provider?.name || ""}
          >
            {service.provider?.name || "—"}
          </div>
        );
      case "date":
        return <Text>{formatShortDate(service.performed_at)}</Text>;
      case "odometer":
        return <Text>{numberWithCommas(service.odometer)}km</Text>;
      case "total":
        return formatPrice(service.total);
      case "status":
        return (
          <InlineStack gap="150" blockAlign="center">
            <StatusBadge status={service.status} />
            {service.invoice_status === "pending" &&
              !["awaiting_provider", "awaiting_invoice"].includes(service.status) && (
              <Badge tone="critical" size="small">
                Falta factura
              </Badge>
            )}
            {service.invoice_status === "cancelled" && (
              <Badge tone="critical" size="small">
                Factura cancelada
              </Badge>
            )}
          </InlineStack>
        );
      default:
        return null;
    }
  };

  const rowMarkup = services.map((service, index) => (
    <IndexTable.Row
      id={service.id}
      key={service.id}
      position={index}
      onClick={() => navigate(`/services/${service.id}`)}
    >
      {visibleColumns.map((key) => (
        <IndexTable.Cell key={key}>{renderCell(key, service)}</IndexTable.Cell>
      ))}
    </IndexTable.Row>
  ));

  const tableComponent = () => (
    <LegacyCard>
      <IndexTable
        resourceName={{ singular: "Servicio", plural: "Servicios" }}
        selectable={false}
        itemCount={services.length}
        headings={visibleColumns.map((key) => ({
          title: COLUMN_DEFS.find((c) => c.key === key)?.title || "",
        }))}
      >
        {rowMarkup}
      </IndexTable>

      {/* Pagination controls */}
      <div
        style={{ padding: "1rem", display: "flex", justifyContent: "center" }}
      >
        <Pagination
          hasPrevious={page > 1}
          onPrevious={() => setPage((p) => Math.max(1, p - 1))}
          hasNext={services.length === limit}
          onNext={() => setPage((p) => p + 1)}
        />
      </div>
    </LegacyCard>
  );

  return (
    <AppWrapper>
      <Page
        title={providerId ? "Servicios de Proveedor" : "Servicios"}
        primaryAction={
          <Button
            variant="primary"
            url="/services/new"
            disabled={!hasPermission("services.create")}
          >
            Crear Servicio
          </Button>
        }
        secondaryActions={
          <InlineStack gap="200">
            <Button onClick={openPending} disabled={!hasPermission("services.update")}>
              Por Pagar
            </Button>
            <Button
              onClick={openMissingInvoices}
              disabled={!hasPermission("services.update")}
            >
              Falta Factura
            </Button>
            <Button
              variant="secondary"
              url="/services/new_field"
              disabled={!hasPermission("services.create")}
            >
              Servicio por liga
            </Button>
            <Button
              variant="secondary"
              url="/services/new_order"
              disabled={!hasPermission("services.create")}
            >
              Crear Orden
            </Button>
          </InlineStack>
        }
        backAction={{ onAction: () => navigate(-1) }}
      >
        <div
          style={{
            marginBottom: "1rem",
            display: "flex",
            gap: "0.5rem",
            alignItems: "center",
          }}
        >
          <div style={{ flexGrow: 1 }}>
            <TextField
              label="Buscar servicios"
              labelHidden
              value={searchInput}
              onChange={setSearchInput}
              clearButton
              onClearButtonClick={() => setSearchInput("")}
              placeholder="Buscar por placa, VIN, modelo, marca o proveedor…"
              autoComplete="off"
            />
          </div>
          <Popover
            active={statusPopoverOpen}
            activator={
              <Button disclosure onClick={() => setStatusPopoverOpen((v) => !v)}>
                {statusFilter.length ? `Status (${statusFilter.length})` : "Status"}
              </Button>
            }
            onClose={() => setStatusPopoverOpen(false)}
          >
            <div style={{ padding: "0.75rem 1rem", minWidth: 220 }}>
              <BlockStack gap="200">
                <ChoiceList
                  allowMultiple
                  title="Filtrar por status"
                  choices={STATUS_CHOICES}
                  selected={statusFilter}
                  onChange={handleStatusChange}
                />
                {statusFilter.length > 0 && (
                  <Button variant="plain" onClick={() => handleStatusChange([])}>
                    Mostrar todos
                  </Button>
                )}
              </BlockStack>
            </div>
          </Popover>
          <Popover
            active={columnsPopoverOpen}
            activator={
              <Button
                icon={SettingsIcon}
                onClick={() => setColumnsPopoverOpen((v) => !v)}
              >
                Columnas
              </Button>
            }
            onClose={() => setColumnsPopoverOpen(false)}
          >
            <div style={{ padding: "0.75rem 1rem", minWidth: 200 }}>
              <ChoiceList
                allowMultiple
                title="Mostrar columnas"
                choices={COLUMN_DEFS.map((c) => ({
                  label: c.required ? `${c.title} (requerida)` : c.title,
                  value: c.key,
                  disabled: c.required,
                }))}
                selected={visibleColumns}
                onChange={handleColumnsChange}
              />
            </div>
          </Popover>
        </div>
        {loading ? <Spinner size="small" /> : services.length === 0 && (search || statusFilter.length) ? (
          <Text tone="subdued">
            {search ? `Sin resultados para “${search}”.` : "Sin servicios con ese status."}
          </Text>
        ) : tableComponent()}

        <Modal
          open={pendingOpen}
          onClose={() => setPendingOpen(false)}
          title="Servicios Por Pagar"
          large
          primaryAction={
            speiEnabled() && hasPermission("services.pay")
              ? {
                  content: speiSubmitting
                    ? "Enviando…"
                    : `Pagar (${payableRows().length})`,
                  onAction: paySelectedWithSpei,
                  loading: speiSubmitting,
                  disabled: pendingLoading || payableRows().length === 0,
                }
              : undefined
          }
        >
          <Modal.Section>
            {pendingLoading ? (
              <div style={{ padding: 24, textAlign: "center" }}>
                <Spinner size="small" />
              </div>
            ) : pendingRows.length === 0 ? (
              <Text tone="subdued">Sin servicios por pagar.</Text>
            ) : (
              <div style={{ overflowX: "auto" }}>
                {speiEnabled() && funds && (
                  <div style={{ marginBottom: 12 }}>
                    <Banner tone={fundsShort() ? "critical" : "info"}>
                      <p>
                        Saldo disponible: <b>{funds.available_display}</b> ·
                        Seleccionado:{" "}
                        <b>{formatPrice(selectedTotalCents() / 100)}</b>
                        {fundsShort() && (
                          <>
                            {" "}
                            — no alcanza. Deselecciona algunos pagos o fondea la
                            cuenta.
                          </>
                        )}
                      </p>
                    </Banner>
                  </div>
                )}
                {speiResult && (
                  <div style={{ marginBottom: 12 }}>
                    <Banner
                      tone={speiResult.failed.length === 0 ? "success" : "warning"}
                      title={
                        speiResult.ok > 0
                          ? `${speiResult.ok} pago(s) enviados por SPEI`
                          : "No se envió ningún pago"
                      }
                      onDismiss={() => setSpeiResult(null)}
                    >
                      {speiResult.failed.length > 0 && (
                        <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                          {speiResult.failed.map((f, i) => (
                            <li key={i}>
                              {f.payable_id ? `Servicio ${f.payable_id}: ` : ""}
                              {messageFor(f.error, f.message)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </Banner>
                  </div>
                )}
                {dispersionIssues.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <Banner tone="warning" title={`${dispersionIssues.length} servicio(s) se omitirán por datos inválidos`}>
                      <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                        {dispersionIssues.map((x, i) => (
                          <li key={i}>
                            {x.s.provider?.name || "?"} ({x.s.invoice_id || "s/folio"}): {x.reasons.join(", ")}
                          </li>
                        ))}
                      </ul>
                    </Banner>
                  </div>
                )}
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f4f4f4", textAlign: "left" }}>
                      <th style={{ padding: "8px 10px", borderBottom: "1px solid #ccc", width: 36, textAlign: "center" }}>
                        <Checkbox
                          label="Seleccionar todos"
                          labelHidden
                          checked={allSelected ? true : someSelected ? "indeterminate" : false}
                          onChange={toggleAll}
                        />
                      </th>
                      <th style={{ padding: "8px 10px", borderBottom: "1px solid #ccc" }}>Proveedor</th>
                      <th style={{ padding: "8px 10px", borderBottom: "1px solid #ccc" }}>Factura</th>
                      <th style={{ padding: "8px 10px", borderBottom: "1px solid #ccc", textAlign: "right" }}>Total</th>
                      <th style={{ padding: "8px 10px", borderBottom: "1px solid #ccc" }}>Placa</th>
                      {speiEnabled() && (
                        <th style={{ padding: "8px 10px", borderBottom: "1px solid #ccc" }}>Cuenta</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRows.map((r, i) => {
                      const total = r.total != null ? Math.round(Number(r.total) * 100) / 100 : null;
                      const cells = [
                        { key: `${i}-name`, value: r.provider?.name || "" },
                        { key: `${i}-invoice`, value: r.invoice_id || "" },
                        { key: `${i}-total`, value: total != null ? total.toFixed(2) : "", align: "right" },
                        { key: `${i}-plate`, value: r.car?.plate || r.sublease_plate || "" },
                      ];
                      return (
                        <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                          <td style={{ padding: "8px 10px", textAlign: "center" }}>
                            <Checkbox
                              label={`Seleccionar ${r.invoice_id || r.id}`}
                              labelHidden
                              checked={selectedIds.has(r.id)}
                              disabled={!isSelectable(r)}
                              onChange={() => toggleId(r.id)}
                            />
                          </td>
                          {cells.map((c) => (
                            <td
                              key={c.key}
                              onClick={() => copyValue(c.value, c.key)}
                              style={{
                                padding: "8px 10px",
                                cursor: "pointer",
                                textAlign: c.align || "left",
                                background: copiedKey === c.key ? "#d4f4dd" : "transparent",
                                transition: "background 200ms",
                              }}
                              title={`Click para copiar: ${c.value}`}
                            >
                              {c.value || <span style={{ color: "#bbb" }}>—</span>}
                            </td>
                          ))}
                          {speiEnabled() && (
                            <td style={{ padding: "8px 10px" }}>
                              <ClabeBadge
                                status={r.provider?.clabe_status}
                                holderName={r.provider?.bank_holder_name}
                              />
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <Text tone="subdued" variant="bodySm">
                  Click en cualquier celda para copiar al portapapeles.
                </Text>
              </div>
            )}
          </Modal.Section>
        </Modal>

        <Modal
          open={missingOpen}
          onClose={() => setMissingOpen(false)}
          title="Servicios sin factura"
          large
          secondaryActions={[
            { content: "Cerrar", onAction: () => setMissingOpen(false) },
          ]}
        >
          <Modal.Section>
            {missingLoading ? (
              <Spinner size="small" />
            ) : missingRows.length === 0 ? (
              <Text tone="subdued">
                No hay servicios esperando factura.
              </Text>
            ) : (
              <BlockStack gap="300">
                <Text tone="subdued" variant="bodySm">
                  Pagados o autorizados sin CFDI. El proveedor debe emitir la
                  factura por el monto exacto que se cotizó y pagó.
                </Text>
                {missingRows.map((r) => {
                  const days = Math.floor(
                    (Date.now() - new Date(r.created_at).getTime()) / 86400000
                  );
                  return (
                    <div
                      key={r.id}
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(`/services/${r.id}`)}
                    >
                      <LegacyCard sectioned subdued>
                        <InlineStack align="space-between" blockAlign="center">
                          <BlockStack>
                            <Text variant="bodyMd">
                              <strong>#{r.id}</strong>{" "}
                              {r.car?.plate || r.sublease_plate || "—"} ·{" "}
                              {r.provider?.name || "—"}
                            </Text>
                            <Text tone="subdued" variant="bodySm">
                              {formatMxDate(r.created_at)} ·{" "}
                              {formatPrice(r.quoted_total)}
                            </Text>
                          </BlockStack>
                          <Badge tone={days >= 30 ? "critical" : "warning"}>
                            {`${days} día(s)`}
                          </Badge>
                        </InlineStack>
                      </LegacyCard>
                    </div>
                  );
                })}
              </BlockStack>
            )}
          </Modal.Section>
        </Modal>
      </Page>
    </AppWrapper>
  );
}

export default AllServices;
